// RadioGroupField.tsx
import { RadioGroup, Radio, Label, RadioGroupProps, RadioProps } from "react-aria-components";
import { ReactNode, useCallback } from "react";

type Option<T extends string> = {
	value: T;
	label: ReactNode;
	disabled?: boolean;
};

interface FieldProps<T extends string> extends Omit<RadioGroupProps, "children" | "value" | "onChange"> {
	options: Option<T>[];
	value: T;
	onChange: (value: T) => void;
	description?: ReactNode;
	errorMessage?: ReactNode;
	label?: string;
}

export function RadioGroupField<T extends string>({
	label,
	options,
	description,
	errorMessage,
	value,
	onChange,
	orientation,
	...rest
}: FieldProps<T>) {
	const handleChange = useCallback((val: string) => onChange(val as T), [onChange]);

	return (
		<RadioGroup
			value={value}
			onChange={handleChange}
			orientation={orientation}
			{...rest}
			className={`flex ${orientation === "vertical" ? "flex-col" : ""} gap-2`}
		>
			{label && <Label className="text-sm font-medium text-gray-900">{label}</Label>}

			{options.map(({ value: v, label: l, disabled }) => (
				<RadioOption key={v} value={v} isDisabled={disabled}>
					{l}
				</RadioOption>
			))}

			{description && <span slot="description">{description}</span>}
			{errorMessage && <span slot="errorMessage">{errorMessage}</span>}
		</RadioGroup>
	);
}

type RadioOptionProps<T extends string> = RadioProps & { value: T };

function RadioOption<T extends string>({ children, ...props }: RadioOptionProps<T>) {
	return (
		<Radio
			{...props}
			className={({ isSelected }) =>
				["flex items-center gap-3 text-sm cursor-pointer text-Text-primary", isSelected && "font-semibold"].join(" ")
			}
		>
			{({ isSelected, isDisabled }) => (
				<>
					<span
						aria-hidden
						className={[
							"h-6 w-6 rounded-[5px] transition",
							isSelected
								? "border-Surface-action-primary bg-white border-5 hover:border-Surface-action-primary-hover"
								: "border-Border-primary bg-white border-2 hover:bg-Surface-action-secondary-hover hover:border-Border-action-primary-hover",
							isSelected && isDisabled && "!bg-Icon-on-disabled !border-Border-disabled !border-5 ",
							isDisabled && !isSelected && "hover:!border-Border-primary hover:!bg-white",
						].join(" ")}
					/>
					{children}
				</>
			)}
		</Radio>
	);
}
